// src/pages/PropertyDetails.jsx
import React, { useState } from 'react';
import { useParams, Link, useNavigate, NavLink, Outlet } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PROPERTY, DELETE_PROPERTY, SUBMIT_PROPERTY, APPROVE_PROPERTY, REJECT_PROPERTY } from '../graphql/properties';
import { usePermissions } from '../shared/hooks/usePermissions';

const TABS = [
    { label: 'Lease', path: 'lease' },
    { label: 'Compliance', path: 'compliance' },
    { label: 'Maintenance', path: 'maintenance' },
    { label: 'Messaging', path: 'messaging' },
    { label: 'Notes', path: 'notes' }
];

const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  const { loading, error, data, refetch } = useQuery(GET_PROPERTY, {
    variables: { id },
    fetchPolicy: 'network-only'
  });

  const [submitProperty] = useMutation(SUBMIT_PROPERTY, {
    onCompleted: () => refetch(),
    onError: err => alert('Error: ' + err.message)
  });

  const [approveProperty] = useMutation(APPROVE_PROPERTY, {
    onCompleted: () => refetch(),
    onError: err => alert('Error: ' + err.message)
  });

  const [rejectProperty] = useMutation(REJECT_PROPERTY, {
    onCompleted: () => { refetch(); setShowRejectInput(false); setRejectReason(''); },
    onError: err => alert('Error: ' + err.message)
  });

  const [deleteProperty] = useMutation(DELETE_PROPERTY, {
    onCompleted: () => navigate('/properties'),
    onError: error => {
      console.error('Error deleting property:', error);
      alert('Failed to delete property. Please try again.');
    }
  });

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${data?.property?.referenceNumber || 'this property'}? This action cannot be undone.`)) {
      deleteProperty({ variables: { id } });
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <strong className="font-bold">Error!</strong>
      <span className="block sm:inline"> {error.message}</span>
    </div>
  );

  const { property } = data;

  const getStatusColor = (status) => {
    switch (status) {
      case 'occupied': return 'bg-green-100 text-green-800';
      case 'vacant': return 'bg-yellow-100 text-yellow-800';
      case 'maintenance': return 'bg-red-100 text-red-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = dateString => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date format';
      return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'numeric', day: 'numeric' });
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Property Details
        </h1>
        <Link to="/properties" className="flex items-center text-blue-600 hover:text-blue-800">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Properties
        </Link>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex space-x-3">
        <Link
          to={`/properties/edit/${property.id}`}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit
        </Link>
        <Link
          to={`/properties/${property.id}/documents`}
          className="inline-flex items-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Documents
        </Link>
        <button
          onClick={handleDelete}
          className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Delete
        </button>
      </div>

      {/* Property Info */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div className="flex items-center">
            <div className="bg-blue-100 text-blue-700 rounded-full h-16 w-16 flex items-center justify-center text-2xl font-bold mr-4">
              {property.referenceNumber?.substring(0, 2) || 'PR'}
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{property.referenceNumber || 'Property'}</h3>
              <p className="mt-1 text-sm text-gray-500">{property.address?.street}</p>
              <p className="text-sm text-gray-500">{property.address?.suburb}, {property.address?.city} {property.address?.postalCode}</p>
            </div>
          </div>
          <div className="text-right">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(property.status)}`}>
              {property.status?.charAt(0).toUpperCase() + property.status?.slice(1)}
            </span>
          </div>
        </div>

        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Full Address</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {property.address?.street}<br />
                {property.address?.suburb}, {property.address?.city} {property.address?.postalCode}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Reference Number</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{property.referenceNumber || 'Not specified'}</dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(property.status)}`}>
                  {property.status?.charAt(0).toUpperCase() + property.status?.slice(1)}
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Property Details */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Property Details</h3>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Bedrooms</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{property.bedrooms || 'Not specified'}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Bathrooms</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{property.bathrooms || 'Not specified'}</dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Garages</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{property.garages || 'Not specified'}</dd>
            </div>
            {property.description && (
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{property.description}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Associated People */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Associated People</h3>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Landlord</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {property.landlord ? (
                  <Link to={`/landlords/${property.landlord.id}`} className="text-blue-600 hover:underline">
                    {property.landlord.firstName} {property.landlord.lastName}
                  </Link>
                ) : 'Not assigned'}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Tenant</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {property.tenant ? (
                  <Link to={`/tenants/${property.tenant.id}`} className="text-blue-600 hover:underline">
                    {property.tenant.firstName} {property.tenant.lastName}
                  </Link>
                ) : 'Not assigned'}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Agent</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {property.agent ? (
                  <Link to={`/agents/${property.agent.id}`} className="text-blue-600 hover:underline">
                    {property.agent.firstName} {property.agent.lastName}
                  </Link>
                ) : 'Not assigned'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Submission Status */}
      {(permissions.canSubmitProperty || permissions.canApproveProperty) && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Submission Status</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">Rental asset approval workflow</p>
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              property.submissionStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
              property.submissionStatus === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
              property.submissionStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {property.submissionStatus || 'DRAFT'}
            </span>
          </div>

          {property.submissionStatus === 'REJECTED' && property.rejectionReason && (
            <div className="border-t border-gray-200 px-4 py-4 bg-red-50">
              <p className="text-sm text-red-700"><span className="font-medium">Rejection reason:</span> {property.rejectionReason}</p>
            </div>
          )}

          <div className="border-t border-gray-200 px-4 py-4 flex flex-wrap gap-3">
            {permissions.canSubmitProperty && (property.submissionStatus === 'DRAFT' || property.submissionStatus === 'REJECTED') && (
              <button
                onClick={() => submitProperty({ variables: { id } })}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Submit for Approval
              </button>
            )}
            {permissions.canApproveProperty && property.submissionStatus === 'PENDING' && (
              <>
                <button
                  onClick={() => approveProperty({ variables: { id } })}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  Approve
                </button>
                {!showRejectInput ? (
                  <button
                    onClick={() => setShowRejectInput(true)}
                    className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                  >
                    Reject
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      placeholder="Reason for rejection..."
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500 w-64"
                    />
                    <button
                      onClick={() => rejectReason.trim() && rejectProperty({ variables: { id, reason: rejectReason } })}
                      disabled={!rejectReason.trim()}
                      className="px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => { setShowRejectInput(false); setRejectReason(''); }}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-6" aria-label="Property workspace tabs">
            {TABS.map(tab => (
              <NavLink
                key={tab.path}
                to={tab.path}
                className={({ isActive }) =>
                  `whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <Outlet />

      {/* System Info */}
      <div className="mt-4 text-sm text-gray-500">
        <p>Created: {formatDate(property.createdAt)} • Last Updated: {formatDate(property.updatedAt)}</p>
      </div>
    </div>
  );
};

export default PropertyDetails;
